import { $api } from "@/shared/api/api";
import { AuthResponse } from "@/shared/types/auth";
import {
  AvatarUploadResponse,
  ChangeEmailRequest,
  ChangePasswordRequest,
  IProfileUser,
  IUpdateAvatar,
  IUpdateEmail,
  IUpdatePassword,
  IUpdateUsername,
  PasswordChangeResponse,
  ProcessOtpRequest,
  ProfileResponse,
} from "@/shared/types/profile";
import AuthService from "./AuthService";

function mapAuthResponseToProfileUser(data: AuthResponse): IProfileUser {
  return {
    id: data.id,
    email: data.email,
    username: data.first_name ?? data.last_name ?? undefined,
    first_name: data.first_name,
    last_name: data.last_name,
    birth_date: data.birth_date,
    gender: data.gender,
    avatar: data.avatar ?? undefined,
    social_media: data.social_media,
  };
}

export default class ProfileService {
  // Получение профиля пользователя через /web/user/me/
  static async getProfile(): Promise<ProfileResponse> {
    const data = await AuthService.checkAuth();
    return {
      user: mapAuthResponseToProfileUser(data),
      message: "Профиль успешно загружен",
    };
  }

  // Обновление имени пользователя (для mutation)
  // Использует /web/user/{id}/ (PATCH) согласно новой документации
  static async updateUsername(data: IUpdateUsername): Promise<ProfileResponse> {
    // Сначала получаем текущего пользователя для получения ID
    const profile = await this.getProfile();
    const userId = profile.user.id;

    const response = await $api.patch<AuthResponse>(`/web/user/${userId}/`, {
      username: data.username,
    });

    return {
      user: mapAuthResponseToProfileUser(response.data),
      message: "Имя пользователя успешно обновлено",
    };
  }

  // Обновление email адреса (для mutation)
  // Использует /web/user/change_email/ (POST) согласно новой документации
  static async updateEmail(data: IUpdateEmail): Promise<ProfileResponse> {
    const requestData: ChangeEmailRequest = {
      new_email: data.email,
    };

    await $api.post<{ new_email: string }>(
      `/web/user/change_email/`,
      requestData,
    );

    // После смены email получаем обновленный профиль
    const profile = await this.getProfile();
    return {
      user: profile.user,
      message: "Email адрес обновлен. Проверьте почту для подтверждения.",
    };
  }

  // Изменение пароля (для mutation)
  // Использует /web/user/change_password/ (POST) согласно новой документации
  static async changePassword(
    data: IUpdatePassword,
  ): Promise<PasswordChangeResponse> {
    // Валидация на фронтенде
    if (data.confirmPassword && data.new_password !== data.confirmPassword) {
      throw new Error("Новый пароль и подтверждение не совпадают");
    }

    const requestData: ChangePasswordRequest = {
      old_password: data.old_password,
      new_password: data.new_password,
    };

    await $api.post(`/web/user/change_password/`, requestData);

    return {
      message: "Пароль успешно изменен",
      success: true,
    };
  }

  // Загрузка аватарки (для mutation)
  // Использует /web/user/{id}/ (PATCH) с multipart/form-data согласно новой документации
  static async uploadAvatar(
    data: IUpdateAvatar,
  ): Promise<AvatarUploadResponse> {
    // Валидация файла
    if (!data.avatar) {
      throw new Error("Файл аватарки не выбран");
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (data.avatar.size > maxSize) {
      throw new Error("Размер файла не должен превышать 5MB");
    }

    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(data.avatar.type)) {
      throw new Error("Поддерживаются только форматы: JPEG, PNG, GIF, WebP");
    }

    // Сначала получаем текущего пользователя для получения ID
    const profile = await this.getProfile();
    const userId = profile.user.id;

    // Создаем FormData для multipart/form-data
    const formData = new FormData();
    formData.append("avatar", data.avatar);

    const response = await $api.patch<AuthResponse>(
      `/web/user/${userId}/`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    const avatarUrl = mapAuthResponseToProfileUser(response.data).avatar;

    return {
      avatarUrl: avatarUrl || "",
      message: "Аватарка успешно загружена",
    };
  }

  // Удаление аватарки (для mutation)
  // Использует /web/user/{id}/ (PATCH) с avatar: null
  static async deleteAvatar(): Promise<ProfileResponse> {
    const profile = await this.getProfile();
    const userId = profile.user.id;

    const response = await $api.patch<AuthResponse>(`/web/user/${userId}/`, {
      avatar: null,
    });

    return {
      user: mapAuthResponseToProfileUser(response.data),
      message: "Аватарка успешно удалена",
    };
  }

  // Обработка OTP при активации аккаунта
  // Использует /web/user/process_otp/ (POST) согласно новой документации
  static async processOtp(data: ProcessOtpRequest): Promise<{ otp: string }> {
    const response = await $api.post<{ otp: string }>(
      `/web/user/process_otp/`,
      data,
    );
    return response.data;
  }

  // Обработка OTP при смене email
  // Использует /web/user/process_email_otp/ (POST) согласно новой документации
  static async processEmailOtp(
    data: ProcessOtpRequest,
  ): Promise<{ otp: string }> {
    const response = await $api.post<{ otp: string }>(
      `/web/user/process_email_otp/`,
      data,
    );
    return response.data;
  }
}
